class HeroSlider extends HTMLElement {
  connectedCallback() {
    this.currentSlide = 0;
    this.$container = this.querySelector("[data-hero-container]");
    this.$slides = this.querySelectorAll("[data-hero-slide]");
    this.$btnPrev = this.querySelector("[data-hero-btn-prev]");
    this.$btnNext = this.querySelector("[data-hero-btn-next]");
    this.$bullets = this.querySelectorAll("[data-hero-bullet]");

    // Make slides programmatically focusable
    [...this.$slides].forEach(($slide) => {
      $slide.setAttribute("tabindex", "-1");
    });

    this.focusIsInside = false;
    this.addEventListener('focusin', (e) => {
      // Always pause the slider when any part of it is focused.
      clearInterval(this.timer);

      // We only track "inside" focus for programmatic focus shifting.
      // Check if the focused element is a descendant of any slide.
      this.focusIsInside = [...this.$slides].some(slide => slide.contains(e.target));
    });

    this.addEventListener('focusout', (e) => {
      // If the newly focused element is not a child of this component,
      // then we consider the focus to have left the component.
      if (!this.contains(e.relatedTarget)) {
        this.focusIsInside = false;
        this.startTimer();
      }
    });

    this.addEventListener('mouseenter', () => {
      clearInterval(this.timer);
    });

    this.addEventListener('mouseleave', () => {
      this.startTimer();
    });

    if (this.$btnPrev) {
      this.$btnPrev.addEventListener("click", () => {
        this.setDatalayerPush('links');
        // update index
        this.currentSlide =
          this.currentSlide > 0 ? this.currentSlide - 1 : this.$slides.length - 1;

        this.scrollToCurrentSlide();
        this.updateSlides();

        clearInterval(this.timer);
      });
    }

    if (this.$btnNext) {
      this.$btnNext.addEventListener("click", () => {
        this.setDatalayerPush('rechts');
        this.gotoNextSlide();
        clearInterval(this.timer);
      });
    }

    // observe slides with intersection observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // update index
            this.currentSlide = [...this.$slides].findIndex(
              ($slide) => $slide === entry.target
            );
            this.updateSlides();
          }
        });
      },
      {
        threshold: 0.5,
      }
    );

    [...this.$slides].forEach(($slide) => {
      observer.observe($slide);
    });

    this.updateSlides();

    this.startTimer();
  }

  setDatalayerPush(direction) {
    const layerConf = JSON.parse(this.$slides[this.currentSlide]?.dataset?.layerSlider);
    
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'hero_slider_pijl',
      'hero_titel': layerConf?.hero_titel || "",
      'klik_richting': direction
    });
  }

  updateSlides() {
    [...this.$slides].forEach(($slide, i) => {
      if (i === this.currentSlide) {
        $slide.removeAttribute("inert");
        $slide.removeAttribute("aria-hidden");
      } else {
        $slide.setAttribute("inert", "");
        $slide.setAttribute("aria-hidden", "true");
      }
    });

    if (this.$bullets.length > 0) {
      this.updateBullets();
    }

    if (this.focusIsInside) {
      // The `focusIsInside` flag is now only true if focus came from within a slide,
      // so we can directly move focus to the new slide.
      // A small delay is needed to ensure the browser has time
      // to process the 'inert' attribute change before we set focus.
      setTimeout(() => {
        this.$slides[this.currentSlide].focus({ preventScroll: true });
      }, 0);
    }
  }

  gotoNextSlide() {
    // update index
    this.currentSlide =
      this.currentSlide < this.$slides.length - 1 ? this.currentSlide + 1 : 0;
    this.scrollToCurrentSlide();
    this.updateSlides();
  }

  scrollToCurrentSlide() {
    const w = this.$container.getBoundingClientRect().width;

    // scroll to slide
    this.$container.scroll({
      left: w * this.currentSlide,
      behavior: "smooth",
    });
  }

  updateBullets() {
    [...this.$bullets].forEach(($bullet) => {
      $bullet.classList.remove("is-active");
    });
    this.$bullets[this.currentSlide].classList.add("is-active");
  }

  startTimer() {
    // Prevent multiple timers
    clearInterval(this.timer);
    this.timer = setInterval(() => {
      this.gotoNextSlide();
    }, 10000);
  }
}

window.customElements.define("hero-slider", HeroSlider);
